/**
 * Microsite nav — hover/tap-to-expand panel behaviour (#159).
 *
 * Wires up `.whf-microsite-nav` instances:
 *   - Click on .__toggle (the chevron) opens/closes the linked panel.
 *   - Hover on a panel-having item opens the panel after a short delay
 *     (desktop only — disabled when no fine pointer is available).
 *   - Mouse leaving the entire nav closes the open panel after a delay.
 *   - Click outside the nav closes the panel.
 *   - Esc closes the panel.
 *
 * The link itself always navigates; the chevron is a separate tap target
 * to avoid the "tap once to open, tap twice to navigate" ambiguity.
 */

const HOVER_OPEN_DELAY = 100;
const HOVER_CLOSE_DELAY = 200;

const setupNav = ( nav ) => {
	const items = nav.querySelectorAll( '.whf-microsite-nav__item.has-panel' );
	if ( ! items.length ) {
		return;
	}

	const supportsHover = window.matchMedia( '(hover: hover) and (pointer: fine)' ).matches;

	let openItem = null;
	let openTimer = null;
	let closeTimer = null;

	const triggerFor = ( item ) => item.querySelector( '[aria-controls][aria-expanded]' );

	const panelFor = ( item ) => {
		const trigger = triggerFor( item );
		const id = trigger && trigger.getAttribute( 'aria-controls' );
		return id ? nav.querySelector( '#' + id ) : null;
	};

	const openPanel = ( item ) => {
		if ( openItem === item ) {
			return;
		}
		if ( openItem ) {
			closePanel( openItem );
		}
		const panel = panelFor( item );
		const trigger = triggerFor( item );
		if ( ! panel || ! trigger ) {
			return;
		}
		item.classList.add( 'is-open' );
		trigger.setAttribute( 'aria-expanded', 'true' );
		panel.hidden = false;
		openItem = item;

		// Align the cascade under its parent item (not the chevron trigger,
		// which on regular nav items sits to the right of the link label
		// and would push the dropdown under the next item). SCSS reads
		// --ms-nav-cascade-offset on the cascade.
		const cascade = panel.querySelector( '.whf-microsite-nav__cascade' );
		if ( cascade ) {
			const navRect = nav.getBoundingClientRect();
			const itemRect = item.getBoundingClientRect();
			const offset = Math.max( 0, itemRect.left - navRect.left );
			cascade.style.setProperty( '--ms-nav-cascade-offset', offset + 'px' );
		}
	};

	const closePanel = ( item ) => {
		const panel = panelFor( item );
		const trigger = triggerFor( item );
		item.classList.remove( 'is-open' );
		if ( trigger ) {
			trigger.setAttribute( 'aria-expanded', 'false' );
		}
		if ( panel ) {
			panel.hidden = true;
		}
		if ( openItem === item ) {
			openItem = null;
		}
	};

	const closeAll = () => {
		if ( openItem ) {
			closePanel( openItem );
		}
	};

	const clearTimers = () => {
		if ( openTimer ) {
			clearTimeout( openTimer );
			openTimer = null;
		}
		if ( closeTimer ) {
			clearTimeout( closeTimer );
			closeTimer = null;
		}
	};

	items.forEach( ( item ) => {
		const trigger = triggerFor( item );
		if ( ! trigger ) {
			return;
		}

		// Trigger click — chevron on a regular item, or the link-button
		// itself on a placeholder item.
		trigger.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			e.stopPropagation();
			clearTimers();
			if ( item.classList.contains( 'is-open' ) ) {
				closePanel( item );
			} else {
				openPanel( item );
			}
		} );

		if ( supportsHover ) {
			item.addEventListener( 'mouseenter', () => {
				clearTimers();
				openTimer = setTimeout( () => {
					openPanel( item );
				}, HOVER_OPEN_DELAY );
			} );
			item.addEventListener( 'mouseleave', () => {
				if ( openTimer ) {
					clearTimeout( openTimer );
					openTimer = null;
				}
			} );
		}
	} );

	if ( supportsHover ) {
		nav.addEventListener( 'mouseleave', () => {
			if ( openTimer ) {
				clearTimeout( openTimer );
				openTimer = null;
			}
			closeTimer = setTimeout( closeAll, HOVER_CLOSE_DELAY );
		} );
		nav.addEventListener( 'mouseenter', () => {
			if ( closeTimer ) {
				clearTimeout( closeTimer );
				closeTimer = null;
			}
		} );
	}

	// Cascade column toggles — chevron on items with grandchildren. CSS
	// handles desktop hover; this is for tap to open / close on mobile.
	const columnToggles = nav.querySelectorAll( '.whf-microsite-nav__sublink-toggle' );
	columnToggles.forEach( ( toggle ) => {
		toggle.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			e.stopPropagation();
			const item = toggle.closest( '.whf-microsite-nav__column-item' );
			if ( ! item ) {
				return;
			}
			const wasOpen = item.classList.contains( 'is-open' );

			// Close any sibling columns at the same level so the cascade
			// stays single-tracked on tap.
			const parentColumn = item.closest( '.whf-microsite-nav__column' );
			if ( parentColumn ) {
				parentColumn
					.querySelectorAll( ':scope > .whf-microsite-nav__column-item.is-open' )
					.forEach( ( sibling ) => {
						if ( sibling !== item ) {
							sibling.classList.remove( 'is-open' );
							const t = sibling.querySelector( ':scope > .whf-microsite-nav__sublink-toggle' );
							if ( t ) {
								t.setAttribute( 'aria-expanded', 'false' );
							}
						}
					} );
			}

			item.classList.toggle( 'is-open', ! wasOpen );
			toggle.setAttribute( 'aria-expanded', wasOpen ? 'false' : 'true' );
		} );
	} );

	// Outside click — close any open panel.
	document.addEventListener( 'click', ( e ) => {
		if ( ! nav.contains( e.target ) ) {
			closeAll();
		}
	} );

	// Esc — close any open panel.
	document.addEventListener( 'keydown', ( e ) => {
		if ( 'Escape' === e.key ) {
			closeAll();
		}
	} );
};

/**
 * Mobile drill-down — fixed bottom toggle + fullscreen overlay with
 * page-style drill in/out. Each "page" is a <ul data-mobile-page="...">
 * rendered upfront; this just manages which one is active and tracks
 * the stack so the back button knows where to return to.
 */
const setupMobileDrill = ( nav ) => {
	const toggle = nav.querySelector( '[data-microsite-nav-mobile-toggle]' );
	const overlay = nav.querySelector( '[data-microsite-nav-mobile]' );
	const pagesWrap = nav.querySelector( '[data-microsite-nav-mobile-pages]' );
	const title = nav.querySelector( '[data-microsite-nav-mobile-title]' );
	const backBtn = nav.querySelector( '[data-microsite-nav-mobile-back]' );
	const backLabel = nav.querySelector( '[data-microsite-nav-mobile-back-label]' );
	const closeBtn = nav.querySelector( '[data-microsite-nav-mobile-close]' );

	if ( ! toggle || ! overlay || ! pagesWrap ) {
		return;
	}

	const pages = new Map();
	pagesWrap.querySelectorAll( '[data-mobile-page]' ).forEach( ( page ) => {
		pages.set( page.dataset.mobilePage, page );
	} );

	const ROOT_TITLE = title ? title.textContent.trim() : '';
	const stack = [ 'root' ];

	const renderState = () => {
		const currentId = stack[ stack.length - 1 ];
		const parents = new Set( stack.slice( 0, -1 ) );

		pages.forEach( ( page, id ) => {
			page.classList.toggle( 'is-active', id === currentId );
			page.classList.toggle( 'is-parent', parents.has( id ) );
		} );

		const currentPage = pages.get( currentId );

		// The drill pages are absolutely positioned, so size the popup to the
		// active page's content (CSS caps it + scrolls when taller).
		if ( pagesWrap && currentPage ) {
			pagesWrap.style.height = currentPage.scrollHeight + 'px';
		}

		if ( title ) {
			title.textContent = currentPage
				? ( currentPage.dataset.mobilePageTitle || ROOT_TITLE )
				: ROOT_TITLE;
		}

		const hasParent = stack.length > 1;
		if ( backBtn ) {
			backBtn.hidden = ! hasParent;
			if ( hasParent && backLabel ) {
				const parentId = stack[ stack.length - 2 ];
				const parentPage = pages.get( parentId );
				backLabel.textContent = parentPage
					? ( parentPage.dataset.mobilePageTitle || ROOT_TITLE )
					: ROOT_TITLE;
			}
		}
	};

	const openOverlay = () => {
		// Tell other navs (e.g. main header drill-nav) to close.
		document.dispatchEvent( new CustomEvent( 'whf:nav-open', { detail: { source: 'microsite' } } ) );
		overlay.hidden = false;
		// Force a reflow before adding the open class so the slide animates.
		void overlay.offsetWidth;
		overlay.classList.add( 'is-open' );
		toggle.setAttribute( 'aria-expanded', 'true' );
		document.documentElement.classList.add( 'whf-microsite-nav-mobile-open' );
		renderState();
		// Move focus into the overlay (the close button) so keyboard + screen
		// reader users land inside the menu rather than behind it.
		if ( closeBtn ) {
			closeBtn.focus();
		}
	};

	const closeOverlay = ( returnFocus ) => {
		if ( ! overlay.classList.contains( 'is-open' ) ) {
			return;
		}
		overlay.classList.remove( 'is-open' );
		toggle.setAttribute( 'aria-expanded', 'false' );
		document.documentElement.classList.remove( 'whf-microsite-nav-mobile-open' );
		// Return focus to the toggle on user-initiated close (Esc / close button /
		// backdrop), but not when another nav opening triggers the close.
		if ( false !== returnFocus ) {
			toggle.focus();
		}
		// Wait for the slide-down before hiding entirely.
		const onEnd = () => {
			overlay.hidden = true;
			overlay.removeEventListener( 'transitionend', onEnd );
		};
		overlay.addEventListener( 'transitionend', onEnd );
		// Reset to the root page next time the overlay opens.
		stack.length = 0;
		stack.push( 'root' );
	};

	// Close when another nav announces it's opening.
	document.addEventListener( 'whf:nav-open', ( e ) => {
		if ( e.detail && 'microsite' !== e.detail.source ) {
			// Another nav took over — don't steal focus back to our toggle.
			closeOverlay( false );
		}
	} );

	const drillTo = ( pageId ) => {
		if ( ! pages.has( pageId ) ) {
			return;
		}
		stack.push( pageId );
		renderState();
	};

	const drillBack = () => {
		if ( stack.length <= 1 ) {
			return;
		}
		stack.pop();
		renderState();
	};

	toggle.addEventListener( 'click', openOverlay );

	if ( closeBtn ) {
		closeBtn.addEventListener( 'click', closeOverlay );
	}

	// Click the dimmed backdrop (outside the popup panel) to close.
	const panel = nav.querySelector( '[data-microsite-nav-mobile-panel]' );
	overlay.addEventListener( 'click', ( e ) => {
		if ( panel && ! panel.contains( e.target ) ) {
			closeOverlay();
		}
	} );
	if ( backBtn ) {
		backBtn.addEventListener( 'click', drillBack );
	}

	pagesWrap.addEventListener( 'click', ( e ) => {
		const drill = e.target.closest( '[data-mobile-drill]' );
		if ( drill && pagesWrap.contains( drill ) ) {
			e.preventDefault();
			drillTo( drill.dataset.mobileDrill );
		}
	} );

	document.addEventListener( 'keydown', ( e ) => {
		if ( 'Escape' === e.key && overlay.classList.contains( 'is-open' ) ) {
			closeOverlay();
		}
	} );

	// Trap Tab focus within the open overlay so it can't reach the page behind it.
	overlay.addEventListener( 'keydown', ( e ) => {
		if ( 'Tab' !== e.key || ! overlay.classList.contains( 'is-open' ) ) return;
		const scope = panel || overlay;
		const focusable = Array.prototype.slice.call( scope.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
		) ).filter( ( el ) => el.offsetParent !== null );
		if ( ! focusable.length ) return;
		const first = focusable[ 0 ];
		const last = focusable[ focusable.length - 1 ];
		if ( e.shiftKey && document.activeElement === first ) {
			e.preventDefault();
			last.focus();
		} else if ( ! e.shiftKey && document.activeElement === last ) {
			e.preventDefault();
			first.focus();
		}
	} );

	renderState();
};

/**
 * Keep the nav reachable on desktop. The horizontal bar isn't sticky, so
 * once it scrolls up out of view we flag the nav `is-detached` — which
 * reveals the fixed bottom toggle (otherwise mobile-only) and lets its
 * drill overlay open. Mirrors the always-present mobile nav. Only detaches
 * when the bar is ABOVE the viewport (not before it's been reached).
 */
const setupBarVisibility = ( nav ) => {
	const bar = nav.querySelector( '.whf-microsite-nav__bar' );
	if ( ! bar || ! ( 'IntersectionObserver' in window ) ) {
		return;
	}

	const observer = new IntersectionObserver(
		( entries ) => {
			const entry = entries[ 0 ];
			const aboveViewport = ! entry.isIntersecting && entry.boundingClientRect.top < 0;
			nav.classList.toggle( 'is-detached', aboveViewport );
		},
		{ threshold: 0 }
	);
	observer.observe( bar );
};

/**
 * Scroll-spy for on-page anchor items. Highlights the nav item whose target
 * section is currently in view (the topmost one crossing the viewport's
 * middle band). Page/URL items keep their server-rendered current-page active
 * state; external links are never tracked. Uses the same `is-active` class the
 * server applies, so the styling is shared.
 */
const setupScrollspy = ( nav ) => {
	if ( ! ( 'IntersectionObserver' in window ) ) {
		return;
	}

	// Group every anchor link (bar + cascade columns + mobile pages) by hash,
	// so the matching section highlights in all of them at once.
	const linksByHash = new Map();
	const targets = [];
	nav.querySelectorAll( 'a[href^="#"]' ).forEach( ( a ) => {
		const hash = a.getAttribute( 'href' );
		if ( ! hash || hash.length < 2 ) {
			return;
		}
		if ( ! linksByHash.has( hash ) ) {
			const el = document.getElementById( hash.slice( 1 ) );
			if ( ! el ) {
				return;
			}
			linksByHash.set( hash, [] );
			targets.push( el );
		}
		const group = linksByHash.get( hash );
		if ( group ) {
			group.push( a );
		}
	} );

	if ( ! targets.length ) {
		return;
	}

	const inView = new Set();
	let currentHash = null;

	// The collapsed "detached" bottom toggle otherwise shows only a static menu
	// label, giving no clue where you are on the page. Mirror the active section
	// onto it so there's a current-location cue without opening the overlay.
	const toggleLabel = nav.querySelector( '[data-microsite-nav-mobile-toggle] span:not(.screen-reader-text)' );

	const apply = () => {
		// Topmost in-view section (document order) wins.
		let hash = null;
		for ( const el of targets ) {
			if ( inView.has( el ) ) {
				hash = '#' + el.id;
				break;
			}
		}
		if ( ! hash || hash === currentHash ) {
			return;
		}
		currentHash = hash;
		linksByHash.forEach( ( links, h ) => {
			links.forEach( ( a ) => a.classList.toggle( 'is-active', h === hash ) );
		} );
		if ( toggleLabel ) {
			const active = linksByHash.get( hash );
			if ( active && active[ 0 ] ) {
				toggleLabel.textContent = active[ 0 ].textContent.trim();
			}
		}
	};

	const observer = new IntersectionObserver(
		( entries ) => {
			entries.forEach( ( e ) => {
				if ( e.isIntersecting ) {
					inView.add( e.target );
				} else {
					inView.delete( e.target );
				}
			} );
			apply();
		},
		// A thin band across the viewport's vertical middle = the "current" line.
		{ rootMargin: '-45% 0px -45% 0px', threshold: 0 }
	);
	targets.forEach( ( el ) => observer.observe( el ) );
};

export const initMicrositeNav = () => {
	document.querySelectorAll( '.whf-microsite-nav[data-microsite-nav]' ).forEach( ( nav ) => {
		setupNav( nav );
		setupMobileDrill( nav );
		setupBarVisibility( nav );
		setupScrollspy( nav );
	} );
};
